import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { KeycloakService } from 'keycloak-angular';

import { CoreModule } from '@app/core';
import { AuthService } from '@app/core/auth';

import { SharedModule } from '@app/shared';

import { ReportsModule } from '@app/features/reports';
import { PanelsModule } from '@app/features/panels';
import { ProfilesModule } from '@app/features/profiles';
import { UsersModule } from '@app/features/users';
import { TranslationModule } from '@app/features/translations';

import { EntitiesModule } from '@app/features/entities';
import { QueryBuildModule } from '@app/features/query-build';
import { ExportsModule } from '@app/features/exports';
import { QueryBasicModule } from '@app/features/query-basic';
import { PatientRequestsModule } from '@app/features/patient-requests';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { WXRLModule } from './features/wxrl/wxrl.module';
import { LogsViewComponent } from './features/logs/logs-view.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  // Load merged translations from backend (base JSON + DB overrides)
  return new TranslateHttpLoader(http, `${environment.apiUrl}/api/i18n/`, '.json');
}

@NgModule({
  declarations: [AppComponent, LogsViewComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    CoreModule,
    AppRoutingModule,
    ReportsModule,
    PanelsModule,
    TranslationModule,
    ProfilesModule,
    UsersModule,
    EntitiesModule,
    QueryBasicModule,
    QueryBuildModule,
    ExportsModule,
    SharedModule,
    HighlightModule,
    PatientRequestsModule,
    WXRLModule,
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [KeycloakService, AuthService],
      multi: true,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        fullLibraryLoader: () => import('highlight.js'),
        themePath: 'assets/styles/stackoverflow-dark.css',
      },
    },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}

export function initApp(keycloak: KeycloakService, authService: AuthService) {
  return () => {
    console.log(
      `Initializing keycloak on ${environment.keycloakUrl} ${environment.realm} ${environment.clientId}`
    );

    return keycloak
      .init({
        config: {
          url: environment.keycloakUrl,
          realm: environment.realm,
          clientId: environment.clientId,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
        },
        enableBearerInterceptor: true,
        bearerExcludedUrls: [],
      })
      .then((loggedIn) => {
        return new Promise((resolve, reject) => {
          if (!loggedIn) {
            return resolve(true);
          }

          authService.loadUser().subscribe(() => {
            resolve(true);
          });
        });
      });
  };
}
